import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Helpers } from "@core/utils/helpers";
import { AppConstants } from "@core/constants/app";
import dayjs from "dayjs";
import { TranslatePipe } from "@ngx-translate/core";
import { Summary } from "@core/interfaces/entities/summary";
import { Params } from "@angular/router";
import { FeatureFlagsService } from "@core/services/feature-flags.service";
import {
    ENABLE_GENDER_AND_IDENTITY_FIELDS,
    ENABLE_MEDICARE_SAVINGS_PROGRAM,
    ENABLE_RACE_AND_ETHNICITY_FIELDS,
    ENABLE_RELATIONSHIP_TO_ENROLLEE_FIELDS,
} from "@core/constants/feature-flags";

@Component({
    selector: "app-summary",
    templateUrl: "./summary.component.html",
    styleUrls: ["./summary.component.scss"],
    providers: [TranslatePipe],
    standalone: false,
})
export class SummaryComponent {
    private appSummary: Summary;
    @Input() showSectionNavigation = false;
    @Input() showAgreement = false;

    get summary() {
        return this.appSummary;
    }

    @Input() set summary(data: Params) {
        this.applicationYear = dayjs(data.coverageEffectiveDate).year();
        const defaultValue = "-";
        const formFieldsPrefix = `common.formFieldsValues`;
        const yes = this.translate.transform(`${formFieldsPrefix}.defaultRadio.yes`);
        const no = this.translate.transform(`${formFieldsPrefix}.defaultRadio.no`);
        const compiledResidentialAddr = `${data.residentialAddress}
          ${data.residentialAddressExt ? " " + data.residentialAddressExt : ""} ${data.residentialCity},
           ${data.residentialState} ${data.residentialZipcode}`;
        const compiledMailingAddress = !data.mailingAddressIsDifferent
            ? compiledResidentialAddr
            : `${data.mailingAddress}
          ${data.mailingAddressExt ? " " + data.mailingAddressExt : ""} ${data.mailingCity},
           ${data.mailingState} ${data.mailingZipcode}`;
        const compiledRepAddr = !data.signedByRep
            ? defaultValue
            : `${data.repAddress}
          ${data.repAddressExt ? " " + data.repAddressExt : ""} ${data.repCity},
           ${data.repState} ${data.repZipcode}`;
        const raceChoices = this.translate.transform(
            `${this.applicantInfoTranslationPrefix}.content.ethnicitySection.subsections.race.options`,
        );
        const ethnicityChoices = this.translate.transform(
            `${this.applicantInfoTranslationPrefix}.content.ethnicitySection.subsections.ethnicity.options`,
        );
        const mappedRaces = data.raceAnswers?.reduce((result, key) => (!key ? result : `${result}<li>${raceChoices[key].label} </li>`), ``);
        const mappedEthnicity = data.ethnicityAnswers?.reduce(
            (result, key) => (!key ? result : `${result}<li>${ethnicityChoices[key].label} </li>`),
            ``,
        );
        const aoeReasonChoices = this.translate.transform(`common.formFieldsValues.attestationRadio.${this.applicationYear}`);
        const individualRepRelationshipValue = this.showRelationshipToEnrolleeField
            ? {
                  individualRepRelationship: data.individualRepRelationship
                      ? this.translate.transform(
                            `${this.applicantInfoTranslationPrefix}.content.relationshipSection.options.${data.individualRepRelationship}.label`,
                        )
                      : defaultValue,
              }
            : null;
        const eligibilitySource = data.eligibility ?? {
            zipCode: data.residentialZipcode,
            hasMedicare: data.hasMedicare,
            hasPrescription: data.hasPrescription,
            hasMedicaid: data.hasMedicaid,
            assistanceRequired: data.assistanceRequired,
            cinNumber: data.cinNumber,
        };

        this.appSummary = {
            applicantInfo: [
                {
                    firstName: data.firstName,
                    middleInitial: data.middleInitial || defaultValue,
                    lastName: data.lastName,
                    sex: this.translate.transform(`${formFieldsPrefix}.sexRadio.${data.sex.charAt(0)}`),
                    dob: dayjs(data.dob).format("MM/DD/YYYY"),
                },
                {
                    residentialAddress: compiledResidentialAddr,
                },
                {
                    mailingAddress: compiledMailingAddress,
                },
                {
                    homePhone: data.contactHomePhone,
                    cellPhone: data.contactCellPhone || defaultValue,
                    email: data.contactEmail || defaultValue,
                },
            ],
            otherInformationFields: {
                ethnicityAnswers: mappedEthnicity ? `<br><ul>${mappedEthnicity}</ul>` : defaultValue,
                raceAnswers: mappedRaces ? `<br><ul>${mappedRaces}</ul>` : defaultValue,
            },
            authorizedRepFields: {
                signedByRep: data.signedByRep ? yes : no,
                repFirstName: data.repFirstName || defaultValue,
                repLastName: data.repLastName || defaultValue,
                phoneNumber: data.repPhone || defaultValue,
                relationship: data.repRelationship
                    ? this.translate.transform(
                          `${this.applicantInfoTranslationPrefix}.content.relationshipOptionsSection.${data.repRelationship}.label`,
                      )
                    : defaultValue,
                address: compiledRepAddr,
            },
            individualRepRelationshipFields: individualRepRelationshipValue,
            identityFields: {
                ...[
                    this.showGenderAndIdentityField
                        ? {
                              gender: data.gender
                                  ? this.translate.transform(
                                        `${this.applicantInfoTranslationPrefix}.content.genderSection.options.${data.gender}.label`,
                                    )
                                  : defaultValue,
                              genderOther: data.genderOther ? `: ${data.genderOther}` : defaultValue,
                              selfIdentify: data.selfIdentify
                                  ? this.translate.transform(
                                        `${this.applicantInfoTranslationPrefix}.content.selfIdentifySection.options.${data.selfIdentify}.label`,
                                    )
                                  : defaultValue,
                              selfIdentifyOther: data.selfIdentifyOther ? `: ${data.selfIdentifyOther}` : defaultValue,
                          }
                        : null,
                ],
            },

            eligibility: {
                zipCode: eligibilitySource.zipCode || defaultValue,
                medicare: eligibilitySource.hasMedicare ? yes : no,
                prescription: eligibilitySource.hasPrescription ? yes : no,
                medicaid: eligibilitySource.hasMedicaid ? yes : no,
                ...(this.applicationYear < 2027 ? { assistance: eligibilitySource.assistanceRequired ? yes : no } : {}),
                ...(this.applicationYear >= 2027 ? { cinNumber: eligibilitySource.cinNumber || defaultValue } : {}),
            },

            attestationOfEligibility: {
                reason: data.aoeSelections
                    ? this.resolveAOEReasonDisplay(data.aoeSelections, aoeReasonChoices)
                    : `${aoeReasonChoices[data.reason]} ${data.reasonDate ? dayjs(data.reasonDate).format("MM/DD/YYYY") : ""} ${data.reasonDescr ? ": " + data.reasonDescr : ""}`,
            },

            plan: {
                planId: `${data.planSelection.name}[ID# ${data.planSelection.hfId}]`,
                planPremium: `$${data.planSelection.premium.toFixed(2)}${data.planSelection.hasDisclaimer && this.applicationYear < 2027 ? "†" : ""}`,
            },

            language: `${this.translate.transform(`${formFieldsPrefix}.langRadio.${data.preferredLanguage}`)}/${this.translate.transform(`${formFieldsPrefix}.specialFormatRadio.${data.preferredPrint}`)}`,

            medicareInfo: [
                {
                    number: data.medicareNumber,
                },
                {
                    otherCoverage: data.medicareHasOtherCoverage ? yes : no,
                    otherCoverageName: data.medicareOtherCoverageName || defaultValue,
                    coverageId: data.medicareCoverageId || defaultValue,
                    coverageGroup: data.medicareCoverageGroup || defaultValue,
                },
                {
                    maritalStatus:
                        this.translate.transform(`${formFieldsPrefix}.maritalStatus.${data.medicareMaritalStatus}`) || defaultValue,
                    employment: data.medicareIsEmployed ? yes : no,
                    spouseIsEmployed: data.medicareSpouseIsEmployed !== null ? (data.medicareSpouseIsEmployed ? yes : no) : defaultValue,
                    isSalaryBelowLimit:
                        data.medicareIsSalaryBelowLimit !== null ? (data.medicareIsSalaryBelowLimit ? yes : no) : defaultValue,
                    limit: data.medicareMaritalStatus
                        ? this.translate.transform(`common.summary.${data.revision}.salaryBelowLimit.${data.medicareMaritalStatus}`)
                        : "",
                    needCare: data.medicareNeedCare
                        ? this.translate.transform(`${formFieldsPrefix}.needCare.${data.medicareNeedCare}.label`)
                        : defaultValue,
                    helpCare: data.medicareHelpCare
                        ? this.translate.transform(`${formFieldsPrefix}.helpCare.${data.medicareHelpCare}.label`)
                        : defaultValue,
                },
                ...(this.showMedicareSavingsProgram
                    ? [
                          {
                              medicareSavingsProgram: data.medicareSavingsProgramType
                                  ? this.translate.transform(
                                        `${formFieldsPrefix}.medicareSavingsChoices.${data.medicareSavingsProgramType}.label`,
                                    )
                                  : defaultValue,
                          },
                      ]
                    : []),
                {
                    reason: this.translate.transform(`${formFieldsPrefix}.medicareReasons.${data.medicareReason.toLowerCase()}`),
                    sep: data.sepReason
                        ? this.translate.transform(`${formFieldsPrefix}.sepReasons.${Helpers.snakeToCamel(data.sepReason)}`)
                        : defaultValue,
                    sepDescr: data.sepReasonDescr
                        ? `<br>${this.translate.transform("common.formFieldsLabels.sepReasonDescr")}: ${data.sepReasonDescr}`
                        : "",
                    effectiveDate: data.coverageEffectiveDate ? dayjs(data.coverageEffectiveDate).format("MM/DD/YYYY") : "",
                },
            ],

            pcp: [
                {
                    name: data.pcpName || defaultValue,
                    id: data.pcpId || defaultValue,
                },
            ],

            payment: {
                paymentOption: this.translate.transform(`${formFieldsPrefix}.paymentOptionRadio.${data.paymentOption}`),
                benefitsSource: (data.benefitsSource || "").toUpperCase() || defaultValue,
            },
        };
    }

    get medicareSubsections() {
        return Helpers.createIterableArray(this.showMedicareSavingsProgram ? 5 : 4);
    }

    @Input()
    set applicationRevision(applicationRevision: string) {
        this.translationPrefixAttribute = `common.summary.${applicationRevision}`;
    }

    resolveAOEReasonDisplay(aoeData, aoeReasonChoices) {
        return aoeData
            .map(
                (result) =>
                    `${aoeReasonChoices[result.reason]} ${result.date ? dayjs(result.date).format("MM/DD/YYYY") : ""} ${result.description ? `: ` + result.description : ""}<br><br>`,
            )
            .join("");
    }

    get translationPrefix(): string {
        return this.translationPrefixAttribute;
    }

    get showEthnicityAndRaceField() {
        return this.featureFlags.isFeatureEnabled(ENABLE_RACE_AND_ETHNICITY_FIELDS) && this.applicationYear < 2026;
    }

    get showGenderAndIdentityField() {
        return this.featureFlags.isFeatureEnabled(ENABLE_GENDER_AND_IDENTITY_FIELDS);
    }

    get showRelationshipToEnrolleeField() {
        return this.featureFlags.isFeatureEnabled(ENABLE_RELATIONSHIP_TO_ENROLLEE_FIELDS) && this.applicationYear > 2025;
    }

    get showMedicareSavingsProgram() {
        return this.featureFlags.isFeatureEnabled(ENABLE_MEDICARE_SAVINGS_PROGRAM) && this.applicationYear >= 2027;
    }

    @Output() navigationOccurredEvent = new EventEmitter();
    public readonly applicantInfoTranslationPrefix = "pages.applicantInfo";
    private translationPrefixAttribute: string;
    public readonly applicantInfoSubsections = Helpers.createIterableArray(4);
    private applicationYear: number;

    constructor(
        private translate: TranslatePipe,
        private featureFlags: FeatureFlagsService,
    ) {}

    public readonly agreementSubSections = Helpers.createIterableArray(3);

    public emitNavigationOccurred(pageName: string) {
        this.navigationOccurredEvent.emit(AppConstants[`${pageName.toUpperCase()}_PAGE`]);
    }
}
